import { ApiRequestError, isPlanLimitCode } from "@/lib/planLimit";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { IconFilePlus, IconListTree, IconLayoutGrid, IconArrowLeft, IconChevronRight, IconSparkles } from "@tabler/icons-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/lilia-ui/dialog";
import { CreateConfirm } from "@/components/editor/CreateConfirm";
import { Button } from "@/components/lilia-ui/button";
import { Input } from "@/components/lilia-ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/lilia-ui/select";
import { CategoryTilePicker } from "@/components/shared/CategoryTilePicker/CategoryTilePicker";
import { ClassOptionsGrid } from "@/components/shared/ClassOptionsGrid/ClassOptionsGrid";
import {
  DEFAULT_CLASS_FOR_CATEGORY,
  DEFAULT_OPTIONS_FOR_CATEGORY,
  type ClassOptionsValue,
  type DocumentCategory,
} from "@/types/documentClass";
import { useCreateDocument } from "@/hooks/useDocuments";
import { usePublisherProfiles } from "@/hooks/usePublisherProfiles";
import { PublishWith } from "@/components/editor/DocumentStamp/sections/PublishWith";
import { api } from "@/lib/api";
import { useTemplates, useUseTemplate } from "@/hooks/useTemplates";
import { useTranslation } from "@/utils/hooks/useTranslation";
import { cn } from "@/lib/cn";
import { useNoteFirstDocument } from "@/lib/onboarding";

interface NewDocumentDialogProps {
  open: boolean;
  onClose: () => void;
  /** Where it opens: the chooser, or straight on the templates (first run 5a's "From a template"). */
  initialView?: "chooser" | "template";
}

const languages = [
  { text: "English", value: "en" },
  { text: "French", value: "fr" },
  { text: "German", value: "de" },
  { text: "Spanish", value: "es" },
];

const DEFAULT_CATEGORY: DocumentCategory = "article";

/** The dialog's sub-views. `chooser` is the landing — the New action surfaces a
 *  choice (blank-with-settings · from structure · from template · express
 *  blank), per the "don't bypass settings + the wizard" requirement. */
type View = "chooser" | "blank" | "template";

export function NewDocumentDialog({ open, onClose, initialView = "chooser" }: NewDocumentDialogProps) {
  const navigate = useNavigate();
  const { t } = useTranslation("dialogs");
  const { t: tCommon } = useTranslation("common");
  const [view, setView] = useState<View>("chooser");
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);

  // Form state
  const [title, setTitle] = useState(tCommon("untitledDocument"));
  const [language, setLanguage] = useState(languages[0].value);
  const [category, setCategory] = useState<DocumentCategory>(DEFAULT_CATEGORY);
  const [options, setOptions] = useState<ClassOptionsValue>(
    DEFAULT_OPTIONS_FOR_CATEGORY[DEFAULT_CATEGORY],
  );

  const createDocument = useCreateDocument();
  // Written for a publisher (25 Oct 2026): the profile sets the class and options, so "How should it
  // look?" steps aside, and the document is created with it.
  const [profileId, setProfileId] = useState("");
  const { profiles } = usePublisherProfiles({ enabled: view === "blank" });
  const profile = profiles.find((p) => p.id === profileId) ?? null;
  const useTemplate = useUseTemplate();
  // Templates are only needed once the user steps into the template view.
  const { templates, isLoading: templatesLoading } = useTemplates({ enabled: open && view === "template" });

  const isCreating = createDocument.isPending || useTemplate.isPending;

  // Always land back on the chooser (or the view asked for) when the dialog (re)opens, so the
  // choice is never skipped on a second invocation.
  useEffect(() => {
    if (open) {
      setView(initialView);
      setSelectedTemplateId(null);
    }
  }, [open, initialView]);
  const noteFirstDocument = useNoteFirstDocument();

  // When the user picks a category tile, swap the options to the
  // per-category defaults. The user can still override after the swap.
  const handleCategoryChange = (next: DocumentCategory) => {
    setCategory(next);
    setOptions(DEFAULT_OPTIONS_FOR_CATEGORY[next]);
  };

  /** Create blank doc with settings, then open it. */
  const handleCreateAndOpen = async () => {
    try {
      const result = await createDocument.mutateAsync({
        title,
        language,
        documentCategory: category,
        // A profile's class and options are set by the profile itself, just after.
        documentClass: DEFAULT_CLASS_FOR_CATEGORY[category],
        paperSize: options.paperSize,
        fontSize: options.fontSize,
        columns: options.columns,
        balancedColumns: options.balancedColumns,
        sides: options.sides,
        titlePage: options.titlePage,
        orientation: options.orientation,
      });
      if (profile) await api.documents.update(result.document.id, { publisherProfile: profile.id });
      noteFirstDocument("blank");
      onClose();
      navigate(`/d/${result.document.id}`);
    } catch (error) {
      // At the plan's limit the limit dialog explains; this one steps aside.
      if (error instanceof ApiRequestError && isPlanLimitCode(error.code)) onClose();
      else console.error("Failed to create document:", error);
    }
  };

  // Express path — a blank document with no settings, "just go". Secondary
  // affordance only; the default surfaces settings + the wizard.
  const handleExpressBlank = async () => {
    try {
      const result = await createDocument.mutateAsync({ title: tCommon("untitledDocument") });
      noteFirstDocument("blank");
      onClose();
      navigate(`/d/${result.document.id}`);
    } catch (error) {
      // At the plan's limit the limit dialog explains; this one steps aside.
      if (error instanceof ApiRequestError && isPlanLimitCode(error.code)) onClose();
      else console.error("Failed to create blank document:", error);
    }
  };

  const handleSelectTemplate = (templateId: string) => {
    setSelectedTemplateId(templateId);
  };

  /** Create from template, then open it. */
  const handleCreateFromTemplateAndOpen = async () => {
    if (!selectedTemplateId) return;

    try {
      // No title: the template view has no title field, so `title` here would be
      // the blank form's hidden default ("Untitled Document"). Without one the
      // server names the document after the template, as the gallery's Use does.
      const result = await useTemplate.mutateAsync({
        templateId: selectedTemplateId,
      });
      noteFirstDocument("template");
      onClose();
      navigate(`/d/${result.id}`);
    } catch (error) {
      // At the plan's limit the limit dialog explains; this one steps aside.
      if (error instanceof ApiRequestError && isPlanLimitCode(error.code)) onClose();
      else console.error("Failed to create document from template:", error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !isCreating && onClose()}>
      <DialogContent className="max-w-[600px]" data-testid="new-document-dialog">
        <DialogHeader>
          <DialogTitle>
            {view === "chooser"
              ? t("newDocument.title")
              : view === "blank"
                ? t("newDocument.chooseBlankTitle")
                : t("newDocument.chooseTemplateTitle")}
          </DialogTitle>
        </DialogHeader>

        {view === "chooser" && (
          <div className="py-2 flex flex-col gap-3" data-testid="new-document-chooser">
            <p className="text-[13px] text-[var(--lilia-text-muted)] m-0">
              {t("newDocument.chooserSubtitle")}
            </p>

            <ChooserCard
              testId="choose-ai"
              icon={<IconSparkles size={20} />}
              title={t("newDocument.chooseAiTitle")}
              description={t("newDocument.chooseAiDesc")}
              accent
              onClick={() => {
                onClose();
                navigate("/ai");
              }}
            />
            <ChooserCard
              testId="choose-blank"
              icon={<IconFilePlus size={20} />}
              title={t("newDocument.chooseBlankTitle")}
              description={t("newDocument.chooseBlankDesc")}
              onClick={() => setView("blank")}
            />
            <ChooserCard
              testId="choose-structure"
              icon={<IconListTree size={20} />}
              title={t("newDocument.chooseStructureTitle")}
              description={t("newDocument.chooseStructureDesc")}
              onClick={() => {
                onClose();
                navigate("/new-from-structure");
              }}
            />
            <ChooserCard
              testId="choose-template"
              icon={<IconLayoutGrid size={20} />}
              title={t("newDocument.chooseTemplateTitle")}
              description={t("newDocument.chooseTemplateDesc")}
              onClick={() => setView("template")}
            />

            <button
              type="button"
              data-testid="choose-express-blank"
              onClick={handleExpressBlank}
              disabled={createDocument.isPending}
              className="mt-1 self-start text-[12.5px] font-medium text-[var(--lilia-text-muted)] hover:text-[var(--lilia-primary)] transition-colors disabled:opacity-50"
            >
              {t("newDocument.expressBlank")}
            </button>
          </div>
        )}

        {view === "blank" && (
          <div className="py-4 flex flex-col gap-5" data-testid="blank-tab">
            <div>
              <p className="mb-2 font-medium text-[13px] text-[var(--lilia-text)]">
                What are you writing?
              </p>
              <CategoryTilePicker
                value={category}
                onChange={handleCategoryChange}
                showChangeLink={false}
              />
              <p
                className="mt-2 text-[12px] text-[var(--lilia-text-muted)]"
                data-testid="class-summary"
              >
                Class:{" "}
                <strong className="font-mono font-semibold text-[var(--lilia-text)]">
                  {profile?.documentClass ?? DEFAULT_CLASS_FOR_CATEGORY[category]}
                </strong>
              </p>
            </div>

            <PublishWith value={profileId} onChange={setProfileId} />

            {!profile && (
              <div>
                <p className="mb-2 font-medium text-[13px] text-[var(--lilia-text)]">
                  How should it look?
                </p>
                <ClassOptionsGrid value={options} onChange={setOptions} />
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="new-doc-title"
                  className="block mb-1.5 font-medium text-[13px] text-[var(--lilia-text)]"
                >
                  {t("newDocument.titleLabel")}
                </label>
                <Input
                  id="new-doc-title"
                  value={title}
                  onChange={(e) => setTitle(e.currentTarget.value)}
                  placeholder={t("newDocument.documentTitlePlaceholder")}
                />
              </div>

              <div>
                <label
                  htmlFor="new-doc-language"
                  className="block mb-1.5 font-medium text-[13px] text-[var(--lilia-text)]"
                >
                  {t("newDocument.language")}
                </label>
                <Select value={language} onValueChange={(val) => setLanguage(val)}>
                  <SelectTrigger id="new-doc-language">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {languages.map((l) => (
                      <SelectItem key={l.value} value={l.value}>
                        {l.text}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Create + open are one step. */}
            <div data-testid="new-document-mode">
              <CreateConfirm
                disabled={isCreating}
                confirmLabel={
                  isCreating
                    ? "Creating…"
                    : undefined
                }
                onConfirm={handleCreateAndOpen}
              />
            </div>
          </div>
        )}

        {view === "template" && (
          <div className="py-4 flex flex-col gap-4" data-testid="template-tab">
            <div className="min-h-[160px] max-h-[45vh] overflow-y-auto thin-scrollbar">
              {templatesLoading ? (
                <div className="text-center py-12 text-[var(--lilia-text-muted)]">{t("newDocument.loadingTemplates")}</div>
              ) : templates && templates.length > 0 ? (
                <div className="grid grid-cols-2 gap-3">
                  {templates.map((template) => (
                    <div
                      key={template.id}
                      data-testid="new-document-template-tile"
                      data-template-id={template.id}
                      className={cn(
                        "flex items-center gap-3 p-3 border-2 border-[var(--lilia-border)] rounded-lg cursor-pointer transition-all duration-150",
                        // A theme colour: the fixed #f8fafc went near-white under white text in dark theme (3 Nov).
                        "hover:border-[var(--lilia-primary)] hover:bg-[var(--lilia-surface-hover)]",
                        selectedTemplateId === template.id && "border-[var(--lilia-primary)] bg-[var(--lilia-primary-light)]"
                      )}
                      onClick={() => handleSelectTemplate(template.id)}
                    >
                      <div className="w-10 h-10 rounded-lg bg-[var(--lilia-primary)] text-[color:var(--lilia-on-primary)] flex items-center justify-center font-semibold text-base">
                        {template.category?.charAt(0).toUpperCase() || "T"}
                      </div>
                      <div className="flex-1">
                        <strong className="block text-sm text-[var(--lilia-text)]">{template.name}</strong>
                        <span className="text-xs text-[var(--lilia-text-muted)] capitalize">{template.category || "General"}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-[var(--lilia-text-muted)]">
                  {t("newDocument.noTemplates")}
                </div>
              )}
            </div>

            {/* 5b: the guided wizard sits with the templates, as "Build it step by step". */}
            <ChooserCard
              testId="template-step-by-step"
              icon={<IconListTree size={20} />}
              title={t("newDocument.chooseStructureTitle")}
              description={t("newDocument.chooseStructureDesc")}
              onClick={() => {
                onClose();
                navigate("/new-from-structure");
              }}
            />

            {selectedTemplateId && (
              <div data-testid="new-document-mode">
                <CreateConfirm
                  disabled={isCreating}
                  confirmLabel={isCreating ? "Creating…" : undefined}
                  onConfirm={handleCreateFromTemplateAndOpen}
                />
              </div>
            )}
          </div>
        )}

        {view !== "chooser" && (
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setView("chooser")}
              disabled={isCreating}
              data-testid="btn-dialog-back"
            >
              <IconArrowLeft size={15} className="mr-1.5" />
              {t("newDocument.back")}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ───── Chooser card ──────────────────────────────────────────────────────────

interface ChooserCardProps {
  testId: string;
  icon: React.ReactNode;
  title: string;
  description: string;
  onClick: () => void;
  /** Highlight the card (used for the AI "propose a draft" entry). */
  accent?: boolean;
}

function ChooserCard({ testId, icon, title, description, onClick, accent = false }: ChooserCardProps) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={cn(
        "group flex items-center gap-3.5 w-full text-left p-3.5 rounded-xl border transition-colors",
        accent
          ? "border-[var(--lilia-primary)] bg-[var(--lilia-primary-light)] hover:brightness-[0.98]"
          : "border-[var(--lilia-border)] bg-[var(--lilia-surface)] hover:border-[var(--lilia-primary)] hover:bg-[var(--lilia-primary-light)]",
      )}
    >
      <span className="shrink-0 w-10 h-10 rounded-lg flex items-center justify-center bg-[var(--lilia-primary-light)] text-[var(--lilia-primary)]">
        {icon}
      </span>
      <span className="flex-1 min-w-0">
        <span className="block text-[14px] font-semibold text-[var(--lilia-text)]">{title}</span>
        <span className="block text-[12px] text-[var(--lilia-text-muted)] mt-0.5">{description}</span>
      </span>
      <IconChevronRight
        size={16}
        className="shrink-0 text-[var(--lilia-text-tertiary)] group-hover:text-[var(--lilia-primary)] transition-colors"
      />
    </button>
  );
}
